import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  CCard,
  CCardBody,
  CNav,
  CNavItem,
  CNavLink,
  CTabContent,
  CTabPane,
} from '@coreui/react-pro'
import AnalyticsV2Service from 'src/service/AnalyticsV2Service'
import FilterBar, { MAX_RANGE_DAYS, daysBetween, rangeForPreset } from './FilterBar'
import OverviewTab from './OverviewTab'
import ScreensTab from './ScreensTab'
import './AppAnalytics.css'

const TABS = [
  { key: 'overview', label: 'Overview' },
  { key: 'screens', label: 'Screens' },
]

const DEFAULT_PRESET = '28d'

/**
 * Filters live in the URL query string, so a filtered view can be bookmarked or
 * pasted to someone else and comes back identical.
 */
const readFilters = (search) => {
  const params = new URLSearchParams(search)
  const preset = params.get('preset') || DEFAULT_PRESET

  let from = params.get('from')
  let to = params.get('to')

  if (preset !== 'custom' || !from || !to) {
    const range = rangeForPreset(preset === 'custom' ? DEFAULT_PRESET : preset)
    if (range) {
      from = range.from
      to = range.to
    }
  }

  return {
    preset,
    from,
    to,
    platform: params.get('platform') || 'all',
    appVersion: params.get('app_version') || 'all',
    tab: params.get('tab') || 'overview',
    interval: params.get('interval') || 'day',
  }
}

const writeFilters = (filters) => {
  const params = new URLSearchParams()
  params.set('preset', filters.preset)
  if (filters.preset === 'custom') {
    if (filters.from) params.set('from', filters.from)
    if (filters.to) params.set('to', filters.to)
  }
  if (filters.platform && filters.platform !== 'all') params.set('platform', filters.platform)
  if (filters.appVersion && filters.appVersion !== 'all') {
    params.set('app_version', filters.appVersion)
  }
  if (filters.tab && filters.tab !== 'overview') params.set('tab', filters.tab)
  if (filters.interval && filters.interval !== 'day') params.set('interval', filters.interval)
  return params.toString()
}

const AppAnalytics = () => {
  const location = useLocation()
  const navigate = useNavigate()

  const [filters, setFilters] = useState(() => readFilters(location.search))

  const [summary, setSummary] = useState(null)
  const [seriesByMetric, setSeriesByMetric] = useState({})
  const [platforms, setPlatforms] = useState(null)
  const [versions, setVersions] = useState(null)
  const [screens, setScreens] = useState(null)
  const [meta, setMeta] = useState(null)

  const [loading, setLoading] = useState({
    summary: true,
    timeseries: true,
    platforms: true,
    versions: true,
    screens: true,
  })
  const [errors, setErrors] = useState({})
  const [reloadToken, setReloadToken] = useState(0)

  // The API rejects anything over 92 days; say so before the request is made.
  const spanDays = daysBetween(filters.from, filters.to)
  const rangeTooLong = spanDays > MAX_RANGE_DAYS
  const rangeInvalid = !filters.from || !filters.to || spanDays < 1

  const updateFilters = useCallback(
    (next) => {
      setFilters(next)
      navigate({ search: writeFilters(next) }, { replace: true })
    },
    [navigate],
  )

  // A stable identity for the request params, so effects do not re-fire on every
  // unrelated state change.
  const requestKey = useMemo(
    () =>
      [
        filters.from,
        filters.to,
        filters.platform,
        filters.appVersion,
        filters.interval,
        reloadToken,
      ].join('|'),
    [filters.from, filters.to, filters.platform, filters.appVersion, filters.interval, reloadToken],
  )

  const apiFilters = useMemo(
    () => ({
      from: filters.from,
      to: filters.to,
      platform: filters.platform,
      appVersion: filters.appVersion,
    }),
    [filters.from, filters.to, filters.platform, filters.appVersion],
  )

  useEffect(() => {
    if (rangeTooLong || rangeInvalid) {
      setLoading({
        summary: false,
        timeseries: false,
        platforms: false,
        versions: false,
        screens: false,
      })
      return undefined
    }

    let cancelled = false

    setLoading({
      summary: true,
      timeseries: true,
      platforms: true,
      versions: true,
      screens: true,
    })
    setErrors({})

    const done = (key) => {
      if (!cancelled) setLoading((prev) => ({ ...prev, [key]: false }))
    }
    const fail = (key, err) => {
      if (!cancelled) {
        setErrors((prev) => ({ ...prev, [key]: err?.message || 'Request failed.' }))
      }
    }
    // A failed request must never look like "zero traffic" — the tile shows an
    // error state instead of a confident zero.
    const handle = (key, setter) => (res) => {
      if (cancelled) return
      if (res.success) {
        setter(res.data)
        if (res.meta) setMeta(res.meta)
      } else {
        fail(key, res)
      }
    }

    AnalyticsV2Service.getSummary(apiFilters)
      .then(handle('summary', setSummary))
      .catch((err) => fail('summary', err))
      .finally(() => done('summary'))

    // Three series: the sessions one drives the trend chart, and all three feed
    // the KPI sparklines.
    Promise.all(
      ['sessions', 'screen_views', 'active_users'].map((metric) =>
        AnalyticsV2Service.getTimeseries(apiFilters, metric, filters.interval).then((res) => ({
          metric,
          res,
        })),
      ),
    )
      .then((results) => {
        if (cancelled) return
        const next = {}
        results.forEach(({ metric, res }) => {
          if (res.success) {
            next[metric] = res.data.current || []
            if (res.meta) setMeta(res.meta)
          } else {
            fail('timeseries', res)
          }
        })
        setSeriesByMetric(next)
      })
      .catch((err) => fail('timeseries', err))
      .finally(() => done('timeseries'))

    AnalyticsV2Service.getPlatforms(apiFilters)
      .then(handle('platforms', (data) => setPlatforms(data.current || [])))
      .catch((err) => fail('platforms', err))
      .finally(() => done('platforms'))

    AnalyticsV2Service.getAppVersions(apiFilters)
      .then(handle('versions', (data) => setVersions(data.current || [])))
      .catch((err) => fail('versions', err))
      .finally(() => done('versions'))

    AnalyticsV2Service.getTopScreens(apiFilters)
      .then(handle('screens', setScreens))
      .catch((err) => fail('screens', err))
      .finally(() => done('screens'))

    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestKey, rangeTooLong, rangeInvalid])

  const rangeError = rangeInvalid
    ? 'Choose a start date on or before the end date.'
    : rangeTooLong
    ? `Please choose a range of ${MAX_RANGE_DAYS} days or fewer. You have selected ${spanDays} days.`
    : null

  const setTab = (tab) => updateFilters({ ...filters, tab })

  return (
    <div className="av2-page">
      <div className="mb-3">
        <h4 className="mb-1">App Analytics</h4>
        <div className="text-body-secondary small">
          How members use the mobile app, on iPhone and Android.
        </div>
      </div>

      <FilterBar
        filters={filters}
        onChange={updateFilters}
        appVersions={versions}
        meta={meta}
        rangeError={rangeError}
      />

      <CNav variant="tabs" role="tablist" className="av2-tabs">
        {TABS.map((tab) => (
          <CNavItem key={tab.key}>
            <CNavLink
              href="#"
              active={filters.tab === tab.key}
              onClick={(e) => {
                e.preventDefault()
                setTab(tab.key)
              }}
            >
              {tab.label}
            </CNavLink>
          </CNavItem>
        ))}
      </CNav>

      <CTabContent className="pt-4">
        {rangeError ? (
          <CCard>
            <CCardBody className="text-center py-5">
              <div className="fw-semibold mb-1">Adjust the date range to see your data</div>
              <div className="text-body-secondary small">{rangeError}</div>
            </CCardBody>
          </CCard>
        ) : (
          <>
            <CTabPane role="tabpanel" visible={filters.tab === 'overview'}>
              <OverviewTab
                summary={summary}
                timeseries={seriesByMetric.sessions}
                seriesByMetric={seriesByMetric}
                platforms={platforms}
                versions={versions}
                interval={filters.interval}
                loading={loading}
                errors={errors}
                onRetry={() => setReloadToken((t) => t + 1)}
              />
            </CTabPane>

            <CTabPane role="tabpanel" visible={filters.tab === 'screens'}>
              <ScreensTab
                screens={screens}
                filters={apiFilters}
                loading={loading.screens}
                error={errors.screens}
                onRetry={() => setReloadToken((t) => t + 1)}
              />
            </CTabPane>
          </>
        )}
      </CTabContent>
    </div>
  )
}

export default AppAnalytics
